import { io, Socket } from 'socket.io-client';
import { REALTIME_WS_URL } from '../global/environment';
import { rootStore } from '../mobx/rootStore';
import { formatToRawTask } from '../mobx/common';
import { RawProject } from '../mobx/interfaces/project';
import { setSocketConnectedAt } from './realtimeStatus';
import { syncTasksOnSocketConnect } from './syncService';
import { captureException } from './logService';

interface SocketEventPayload {
  fullDocument: {
    _id?: string;
    id?: string;
  };
}

const BUFFER_TIME = 500;

const latestUpdate = new Map<string, () => void>();
const updateTimer = new Map<string, NodeJS.Timeout>();
let socket: Socket | null = null;

const bufferUpdate = (docId: string, updateFn: () => void) => {
  // Always store the latest update function
  latestUpdate.set(docId, updateFn);

  // If a timer already exists, do not reset it
  if (updateTimer.has(docId)) {
    return;
  }

  // Set a new timer to execute the latest stored update
  const timer = setTimeout(() => {
    const latestFn = latestUpdate.get(docId);
    if (latestFn) {
      latestFn(); // Apply the most recent update
      latestUpdate.delete(docId);
    }
    updateTimer.delete(docId);
  }, BUFFER_TIME);

  updateTimer.set(docId, timer);
};

export const connectToWebsocket = (currentWorkspaceId: string, currentUserId: string) => {
  if (socket) {
    socket.disconnect();
  }
  setSocketConnectedAt(null);

  socket = io(REALTIME_WS_URL);
  let hasConnected = false;

  socket.on('connect', () => {
    console.log('Connected to Socket server!');
    setSocketConnectedAt(Date.now());
    socket?.send({ type: 'join_workspace', payload: currentWorkspaceId });
    socket?.send({ type: 'set_user_id', payload: currentUserId });
    // Only catch up on reconnect. After the first connect (app start / workspace switch) appInit has
    // just synced, and the gate already makes the next task page fetch once to cover the tiny gap.
    if (hasConnected) syncTasksOnSocketConnect().catch(captureException);
    hasConnected = true;
  });
  socket.on('disconnect', () => {
    console.log('Disconnected to Socket server!');
    setSocketConnectedAt(null);
  });
  socket.on('connect_error', () => {
    setSocketConnectedAt(null);
  });

  socket.onAny((event: string, payload: SocketEventPayload) => {
    const { fullDocument } = payload;
    if (fullDocument['_id']) {
      fullDocument.id = fullDocument['_id'];
    }
    if (!fullDocument || !fullDocument.id) return;

    bufferUpdate(fullDocument.id, () => handleEvent(event, fullDocument));
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleEvent = (event: string, fullDocument: any) => {
  const currentUserId = rootStore.currentUser?.id;
  const userProjects = rootStore.projectStore.projects.map((project) => project.id);

  if (!currentUserId) return;

  switch (event) {
    // Notifications
    case 'notifications-insert':
    case 'notifications-update':
      const notificationToUpsert = formatRealTimeNotification(fullDocument);
      rootStore.notificationStore.upsertNotifications([notificationToUpsert]);
      break;

    case 'notifications-delete':
      const notificationId = fullDocument.id;
      rootStore.notificationStore.removeNotifications([notificationId]);
      break;

    // Tasks
    case 'tasks-insert':
    case 'tasks-update':
      const taskToUpsert = formatToRawTask(fullDocument);

      if (!userProjects.includes(taskToUpsert.projectId)) {
        return;
      }

      if (taskToUpsert.isArchived) {
        rootStore.taskStore.removeTasks([taskToUpsert.id]);
      } else {
        rootStore.taskStore.upsertTasks([taskToUpsert], { excludeFields: ['timeLogs', 'vercelDeployments'] });
      }
      break;
    case 'tasks-delete':
      const taskId = fullDocument.id;
      rootStore.taskStore.removeTasks([taskId]);
      break;

    // Projects
    case 'projects-insert':
    case 'projects-update':
      const rawProject = fullDocument as RawProject;
      rawProject.isFavorite = rootStore.currentUser?.id
        ? rawProject.favoriteUsers?.includes(rootStore.currentUser.id) || false
        : false;

      const isUserInProject = rawProject.users.includes(currentUserId);
      const wasUserInProject = userProjects.includes(rawProject.id);

      if (isUserInProject || wasUserInProject) {
        if (rawProject?.isArchived || (wasUserInProject && !isUserInProject)) {
          rootStore.projectStore.removeProjects([rawProject.id]);
        } else {
          const formattedRawProjects = formatRealtimeProjectDetail([rawProject]);
          rootStore.projectStore.upsertProjects(formattedRawProjects);
        }
      }

      break;
    case 'projects-delete':
      rootStore.projectStore.removeProjects([fullDocument.id]);
      break;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatRealTimeNotification = (fullDocument: any) => {
  return {
    date: fullDocument?.SnoozeDate ?? fullDocument?.CreatedTime,
    description: fullDocument?.Description,
    id: fullDocument?.['_id'],
    isRead: fullDocument?.IsRead,
    referenceId: fullDocument?.ReferenceId,
    referenceId2: fullDocument?.ReferenceId2,
    referenceId3: fullDocument?.ReferenceId3,
    title: fullDocument?.Title,
    type: fullDocument?.Type,
    userId: fullDocument?.UserId,
    createdBy: fullDocument?.Username === 'Github' ? 'github' : fullDocument?.CreatedBy,
    createdTime: fullDocument?.CreatedTime
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatRealtimeProjectDetail = (data: any): RawProject[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return data?.map((item: any) => {
    const vercelProject = {
      id: item.vercelProject?.id,
      name: item.vercelProject?.Name,
      accountId: item.vercelProject?.AccountId,
      githubRepoId: item.vercelProject?.GithubRepoId
    };
    return {
      ...item,
      vercelProject
    };
  });
};
